import { BaseListViewStore, checkPermissions, BooleanCellData } from 'core/stores';
import { CarrierStore } from 'modules/carrier/stores';
import { LookupStore } from 'modules/lookup/stores';
import { localStorageHandler } from 'core/services';
import { notificationError, TableFilterUtility, checkForColumnWidth, isSuperAdminOrganization } from 'core/utils';
import { observable, action, reaction, makeObservable } from 'mobx';
import { ConfirmModalParams, ModalParams } from 'core/models';
import _ from 'lodash';

class CarrierListViewStore extends BaseListViewStore {
	@observable change = false;
	isSuperAdmin = false;
	showActions = true;
	localStorageWidthKey = 'carriers-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new CarrierStore(rootStore),
			queryParams: {
				embed: ['carrierType', 'seller'],
			},
			tableData: {
				columns: [
					{
						key: 'name',
						title: 'CARRIERVIEW.NAME',
						selected: true,
						width: 200,
						filter: null,
						sortable: true,
						selected: true,
					},
					{
						key: 'seller.applicationTitle',
						title: 'CARRIERVIEW.SELLER',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						selected: true,
						enabled: isSuperAdminOrganization(rootStore),
					},
					{
						key: 'carrierType.name',
						title: 'CARRIERVIEW.CARRIERTYPE',
						selected: true,
						width: 200,
						filter: null,
						sortable: true,
						selected: true,
					},
					{
						key: 'isThirdParty',
						title: 'CARRIERVIEW.ISTHIRDPARTY',
						dataCell: BooleanCellData,
						selected: true,
						width: 200,
						filter: null,
						sortable: true,
						selected: true,
					},
				],
				sortObject: [
					{
						field: 'name',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: async item => this.routes.edit(item.id),
					onDelete: async item => this.openDeleteConfirmModal(item),
				},
			},
			permissions: {
				update: () => this.authStore.hasPermission('shippingCarrierSection.update'),
				create: () => this.authStore.hasPermission('shippingCarrierSection.create'),
				remove: () => this.authStore.hasPermission('shippingCarrierSection.remove'),
				read: () => this.authStore.hasPermission('shippingCarrierSection.read'),
				full: () => this.authStore.hasPermission('shippingCarrierSection.full'),
			},
			routes: {
				edit: id => this.rootStore.routerStore.setQueryParams({ editId: id }),
				create: () => this.rootStore.routerStore.setQueryParams({ create: null }),
				list: () => this.rootStore.routerStore.goTo('master.app.main.carrier.list', {}),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(rootStore)) {
			this.isSuperAdmin = true;
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findCarrier);
		reaction(
			() => this.rootStore.routerStore.routerState,
			state => {
				if (state.routeName !== 'master.app.main.carrier.list') {
					return;
				}

				const { editId, create } = state.queryParams;

				this.editModal.setOpen(editId !== undefined || create !== undefined);
			}
		);
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			service: this.listStore.service,
		});
		this.showActions = checkPermissions(this).actionsAllowed;
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.setButtons();
		this.setSearchVisible();
	}

	@action.bound setAsChanged() {
		this.change = !this.change;
	}

	@action.bound setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'shippingCarrierSection.create',
					text: 'CARRIERVIEW.NEW',
				},
			],
		});
	}

	@action.bound async findCarrier() {
		this.suspend();
		this.queryUtility.setFilterParams(this);
		await this.listStore.find(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.setHeaderModes();
		this.resume();
	}

	@action.bound async deleteCarrier(itemId) {
		this.suspend();
		try {
			await this.listStore.deleteCarrier(itemId);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.CARRIER', { name: this.selectedItem.name }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	@action.bound async openDeleteConfirmModal(item) {
		this.selectedItem = item;
		this.deleteConfirmModal.open({
			message: ['LOOKUPDELETECONFIRM.CARRIER', { name: item.name }],
		});
	}

	@action.bound closeEditModal() {
		this.rootStore.routerStore.updateQueryParams({
			editId: undefined,
			create: undefined,
			...this.queryUtility.getFilterUrlParams(),
		});
	}

	editModal = new ModalParams({
		onClose: this.closeEditModal,
	});

	deleteConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.deleteCarrier(this.selectedItem);
		},
	});
}

export default CarrierListViewStore;



// WEBPACK FOOTER //
// ./src/modules/carrier/stores/CarrierListViewStore.js